import React from "react";
import styled from "styled-components";
import {
  Text,
  FilledButton,
  OutlinedButton,
  theme,
} from "@ifgengineering/component-library";
import { DoubleButtonWrapper, FormContainer } from "./styled";
import Tooltip from "@components/Tooltip";
import { useOnboardingStepChrome } from "./stepChrome";
import { StepLayout } from "./StepLayout";

export interface OnboardingLayoutProps {
  heading: string;
  tooltipTitle?: string;
  tooltipContent?: string | React.ReactElement;
  subheading?: string | JSX.Element;
  helperText?: string | JSX.Element;
  disableBack?: boolean;
  disableNext?: boolean;
  hideNextButton?: boolean;
  hideBackButton?: boolean;
  backBtnText?: string;
  nextBtnText?: string;
  optionalText?: string;
  onSubmit?: React.FormEventHandler<HTMLFormElement>;
  onClickBack?: VoidFunction;
  isSubmitting?: boolean;
  preventBackSubmit?: boolean;
  /** Lets a page move focus to its heading, e.g. after resetting in place. */
  headingId?: string;
  /** Sizes the buttons to their labels so a long one never wraps or clips. */
  fitButtonsToText?: boolean;
  nextTestId?: string;
  backTestId?: string;
}

const PageWrapper = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TitleContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  & > section {
    display: flex;
    align-items: flex-end;
  }
`;

const OnboardingLayout: React.FC<OnboardingLayoutProps> = (props) => {
  const chrome = useOnboardingStepChrome();
  return chrome ? (
    <StepLayout {...props} reassurance={chrome.reassurance} />
  ) : (
    <ClassicLayout {...props} />
  );
};

const ClassicLayout: React.FC<OnboardingLayoutProps> = ({
  children,
  heading,
  tooltipTitle,
  tooltipContent,
  subheading,
  helperText,
  disableBack,
  disableNext,
  backBtnText,
  nextBtnText,
  hideBackButton,
  hideNextButton,
  optionalText,
  onSubmit,
  onClickBack,
  isSubmitting,
  preventBackSubmit,
  headingId,
  fitButtonsToText,
  nextTestId = "submit-button",
  backTestId,
}) => {
  const nextButtonText = nextBtnText || "Save and Proceed";
  const backButtonText = backBtnText || "Back";
  const isSingleButton = hideBackButton || hideNextButton;
  const disabledTextColor = disableNext ? "SLATE400" : "WHITE";
  return (
    <PageWrapper onSubmit={onSubmit}>
      <HeadingContainer>
        {heading && (
          <TitleContainer>
            <Text
              type="H32"
              fontFamily="archiabold"
              color="SLATE900"
              id={headingId}
              tabIndex={headingId ? -1 : undefined}
            >
              {heading}
            </Text>
            {optionalText && (
              <Text type="P16" color="SLATE700">
                {optionalText}
              </Text>
            )}
            {tooltipTitle && tooltipContent && (
              <Tooltip
                label={tooltipTitle}
                openOnMount
                labelFont="S16"
                labelColor="BLUE600"
                text={tooltipContent}
                textColor="SLATE700"
                backgroundColor="GOLD100"
              />
            )}
          </TitleContainer>
        )}
        {subheading && (
          <Text type="P16" color="SLATE700">
            {subheading}
          </Text>
        )}
        {helperText && (
          <Text
            style={{ marginTop: "18px" }}
            type="P16"
            color="SLATE600"
            as="div"
          >
            {helperText}
          </Text>
        )}
      </HeadingContainer>
      <FormContainer isLarge>{children}</FormContainer>
      <DoubleButtonWrapper
        isSingleButton={isSingleButton}
        fitToText={fitButtonsToText}
      >
        {!hideBackButton && (
          <OutlinedButton
            type={preventBackSubmit ? "button" : undefined}
            disabled={disableBack}
            onClick={onClickBack}
            testId={backTestId}
            icon="ArrowLeft"
            height="48px"
            bgColor="none"
            text={backButtonText}
          />
        )}
        {!hideNextButton && (
          <FilledButton
            testId={nextTestId}
            type="submit"
            text={nextButtonText}
            icon={!isSubmitting ? "ArrowRight" : undefined}
            iconPosition="right"
            iconColor={disabledTextColor}
            textColor={disabledTextColor}
            color="navy"
            height="48px"
            disabled={isSubmitting || disableNext}
            isLoading={isSubmitting}
            customBackgroundColors={{
              default: theme.colors.SLATE900,
              hover: theme.colors.SLATE500,
              active: theme.colors.SLATE900,
              selected: theme.colors.SLATE900,
              disabled: theme.colors.SLATE300,
            }}
          />
        )}
      </DoubleButtonWrapper>
    </PageWrapper>
  );
};

export default OnboardingLayout;
